import React, { useState, useEffect, useMemo } from 'react';
import { api } from '../hooks/useApi';
import { formatCurrency, formatDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import OrderModal from '../components/orders/OrderModal';
import {
  ShoppingCart, Plus, Trash2, Receipt, Tag, Loader2, ChevronDown, ChevronRight, FileText,
} from 'lucide-react';

const OrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [advertisers, setAdvertisers] = useState([]);
  const [editions, setEditions] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [salesReps, setSalesReps] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [invoiceFor, setInvoiceFor] = useState(null); // order being invoiced
  const [discountFor, setDiscountFor] = useState(null); // order being discount-edited

  const loadOrders = async () => {
    try { setOrders(await api.get('/orders')); } catch (e) { console.error(e); }
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      const [o, a, e, p, s, st] = await Promise.all([
        api.get('/orders'), api.get('/advertisers'), api.get('/editions'),
        api.get('/product-types'), api.get('/sales-reps'), api.get('/settings'),
      ]);
      setOrders(o); setAdvertisers(a); setEditions(e); setProductTypes(p); setSalesReps(s); setSettings(st);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); }, []);

  const toggleExpand = async (id) => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
    if (!expanded[id]) {
      try {
        const full = await api.get(`/orders/${id}`);
        setOrders(prev => prev.map(o => o.id === id ? { ...o, bookings: full.bookings } : o));
      } catch (e) { /* ignore */ }
    }
  };

  const handleDelete = async (order) => {
    if (!window.confirm(`Delete order for ${order.advertiser_name}? This removes its ${order.item_count} un-invoiced booking(s).`)) return;
    try {
      const res = await api.delete(`/orders/${order.id}`);
      toast.success(`Order deleted (${res.deleted_bookings} bookings removed)`);
      loadOrders();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const statusColor = (s) => s === 'invoiced' ? 'text-emerald-400 bg-emerald-900/20' : 'text-[#3B82F6] bg-[#3B82F6]/10';

  const sorted = useMemo(() => [...orders].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || '')), [orders]);

  return (
    <div className="space-y-6" data-testid="orders-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-[#1E293B] flex items-center justify-center"><ShoppingCart className="w-5 h-5 text-[#3B82F6]" /></div>
          <div>
            <h1 className="text-3xl font-bold text-[#F8FAFC] tracking-tight">Orders</h1>
            <p className="text-[#94A3B8] mt-1">Bundle multiple bookings for a client, apply a discount, then invoice in one click.</p>
          </div>
        </div>
        <Button onClick={() => setShowOrderModal(true)} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="new-order-btn">
          <Plus className="w-4 h-4 mr-2" /> New Order
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40"><Loader2 className="w-8 h-8 animate-spin text-[#3B82F6]" /></div>
      ) : sorted.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 text-[#94A3B8]">
          <ShoppingCart className="w-16 h-16 mb-4 opacity-50" />
          <p className="text-lg">No orders yet</p>
          <Button onClick={() => setShowOrderModal(true)} variant="outline" className="mt-4 border-[#1E293B] text-[#94A3B8] hover:bg-[#1E293B]" data-testid="new-order-empty-btn">Create your first order</Button>
        </div>
      ) : (
        <div className="bg-[#121212] border border-[#1E293B] rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#1E293B] text-[#94A3B8] text-left">
                <th className="py-3 px-4 w-8"></th>
                <th className="py-3 px-4">Client</th>
                <th className="py-3 px-4">Items</th>
                <th className="py-3 px-4">Subtotal</th>
                <th className="py-3 px-4">Discount</th>
                <th className="py-3 px-4">Total</th>
                <th className="py-3 px-4">Status</th>
                <th className="py-3 px-4">Created</th>
                <th className="py-3 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(order => (
                <React.Fragment key={order.id}>
                  <tr className="border-b border-[#1E293B]/60 hover:bg-[#1E293B]/30" data-testid={`order-row-${order.id}`}>
                    <td className="py-3 px-4">
                      <button onClick={() => toggleExpand(order.id)} className="text-[#94A3B8]" data-testid={`order-expand-${order.id}`}>
                        {expanded[order.id] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </button>
                    </td>
                    <td className="py-3 px-4 text-[#F8FAFC] font-medium">{order.advertiser_name}</td>
                    <td className="py-3 px-4 text-[#94A3B8]">{order.item_count}</td>
                    <td className="py-3 px-4 text-[#94A3B8]">{formatCurrency(order.subtotal)}</td>
                    <td className="py-3 px-4 text-emerald-400">
                      {order.discount_amount ? `− ${formatCurrency(order.discount_amount)}` : '—'}
                      {order.discount_type === 'percent' && order.discount_value ? <span className="text-[10px] text-[#475569] ml-1">({order.discount_value}%)</span> : null}
                    </td>
                    <td className="py-3 px-4 text-[#3B82F6] font-semibold">{formatCurrency(order.total)}</td>
                    <td className="py-3 px-4">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${statusColor(order.status)}`}>{order.status}</span>
                      {order.invoice_number && <span className="block text-[10px] text-[#475569] mt-0.5">{order.invoice_number}</span>}
                    </td>
                    <td className="py-3 px-4 text-[#94A3B8]">{formatDate(order.created_at)}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-end gap-1.5">
                        {order.status !== 'invoiced' ? (
                          <>
                            <Button size="sm" onClick={() => setInvoiceFor(order)} className="h-8 bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid={`invoice-order-btn-${order.id}`}>
                              <Receipt className="w-3.5 h-3.5 mr-1" /> Invoice
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setDiscountFor(order)} className="h-8 border-[#1E293B] bg-[#0F0F0F] text-[#94A3B8]" data-testid={`edit-discount-btn-${order.id}`} title="Edit discount">
                              <Tag className="w-3.5 h-3.5" />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleDelete(order)} className="h-8 border-red-900/50 bg-[#0F0F0F] text-red-400 hover:bg-red-900/20" data-testid={`delete-order-btn-${order.id}`} title="Delete order">
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </>
                        ) : (
                          <span className="text-xs text-[#475569] flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> Invoiced</span>
                        )}
                      </div>
                    </td>
                  </tr>
                  {expanded[order.id] && (
                    <tr className="bg-[#0F0F0F]" data-testid={`order-items-${order.id}`}>
                      <td colSpan={9} className="px-6 py-3">
                        {(order.bookings || []).length === 0 ? (
                          <p className="text-xs text-[#475569]">Loading items…</p>
                        ) : (
                          <div className="space-y-1">
                            {order.bookings.map(b => (
                              <div key={b.id} className="flex items-center gap-3 text-xs text-[#94A3B8] py-1 border-b border-[#1E293B]/40 last:border-0">
                                <span className="text-[#F8FAFC] w-48 truncate">{b.product_type}</span>
                                <span className="w-40 truncate">{b.edition_name || b.digital_start_date || '—'}</span>
                                <span className="capitalize">{b.category === 'digital_edition' ? 'print' : b.category}</span>
                                {b.order_line_package_name && <span className="text-[#3B82F6]">· {b.order_line_package_name}</span>}
                                <span className="ml-auto">{b.list_rate && b.list_rate !== b.rate ? <span className="line-through mr-1 text-[#475569]">{formatCurrency(b.list_rate)}</span> : null}<span className="text-[#F8FAFC]">{formatCurrency(b.rate)}</span></span>
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <OrderModal
        isOpen={showOrderModal}
        onClose={() => setShowOrderModal(false)}
        advertisers={advertisers}
        editions={editions}
        productTypes={productTypes}
        salesReps={salesReps}
        settings={settings}
        onSave={() => { setShowOrderModal(false); loadOrders(); toast.success('Order created'); }}
      />

      <InvoiceOrderDialog
        order={invoiceFor}
        onClose={() => setInvoiceFor(null)}
        onDone={() => { setInvoiceFor(null); loadOrders(); }}
      />

      <EditDiscountDialog
        order={discountFor}
        onClose={() => setDiscountFor(null)}
        onDone={() => { setDiscountFor(null); loadOrders(); }}
      />
    </div>
  );
};

const InvoiceOrderDialog = ({ order, onClose, onDone }) => {
  const [sendEmail, setSendEmail] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (order) setSendEmail(false); }, [order]);
  if (!order) return null;
  const submit = async () => {
    setSaving(true);
    try {
      const inv = await api.post(`/orders/${order.id}/invoice`, { send_email: sendEmail, origin_url: window.location.origin });
      toast.success(`Invoice ${inv.invoice_number} created (${formatCurrency(inv.total)})`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invoicing failed');
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={!!order} onOpenChange={onClose}>
      <DialogContent className="bg-[#121212] border-[#1E293B] text-[#F8FAFC] max-w-md" data-testid="invoice-order-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Receipt className="w-5 h-5 text-[#3B82F6]" /> Invoice Order</DialogTitle></DialogHeader>
        <p className="text-sm text-[#94A3B8]">Bill all of <span className="text-[#F8FAFC]">{order.advertiser_name}</span>'s {order.item_count} booking(s) on a single invoice. The order discount is already reflected in the rates.</p>
        <div className="bg-[#0F0F0F] border border-[#1E293B] rounded-md p-3 text-sm space-y-1">
          <div className="flex justify-between"><span className="text-[#94A3B8]">Subtotal</span><span>{formatCurrency(order.subtotal)}</span></div>
          <div className="flex justify-between"><span className="text-[#94A3B8]">Discount</span><span className="text-emerald-400">− {formatCurrency(order.discount_amount)}</span></div>
          <div className="flex justify-between font-semibold"><span>Net (before tax)</span><span className="text-[#3B82F6]">{formatCurrency(order.total)}</span></div>
        </div>
        <label className="flex items-center gap-2 text-sm text-[#94A3B8]">
          <Checkbox checked={sendEmail} onCheckedChange={setSendEmail} data-testid="invoice-order-send-email" /> Email the invoice to the client now
        </label>
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="outline" onClick={onClose} className="border-[#1E293B] text-[#94A3B8]">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="confirm-invoice-order-btn">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating…</> : 'Create Invoice'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const EditDiscountDialog = ({ order, onClose, onDone }) => {
  const [type, setType] = useState('none');
  const [value, setValue] = useState(0);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (order) { setType(order.discount_type || 'none'); setValue(order.discount_value || 0); } }, [order]);
  if (!order) return null;
  const submit = async () => {
    setSaving(true);
    try {
      await api.put(`/orders/${order.id}`, { discount_type: type, discount_value: Number(value) || 0 });
      toast.success('Discount updated');
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={!!order} onOpenChange={onClose}>
      <DialogContent className="bg-[#121212] border-[#1E293B] text-[#F8FAFC] max-w-sm" data-testid="edit-discount-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Tag className="w-5 h-5 text-[#3B82F6]" /> Edit Discount</DialogTitle></DialogHeader>
        <p className="text-sm text-[#94A3B8]">Re-spread the discount across {order.advertiser_name}'s items.</p>
        <div className="flex gap-2">
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="bg-[#0F0F0F] border-[#1E293B] text-[#F8FAFC] w-40" data-testid="edit-discount-type"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#121212] border-[#1E293B] text-[#F8FAFC]">
              <SelectItem value="none">No discount</SelectItem>
              <SelectItem value="percent">Percentage %</SelectItem>
              <SelectItem value="amount">Fixed amount $</SelectItem>
            </SelectContent>
          </Select>
          {type !== 'none' && <Input type="number" min={0} value={value} onChange={(e) => setValue(e.target.value)} className="bg-[#0F0F0F] border-[#1E293B] text-[#F8FAFC]" data-testid="edit-discount-value" />}
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="outline" onClick={onClose} className="border-[#1E293B] text-[#94A3B8]">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="save-discount-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OrdersPage;
